import Sentry from '@/services/sentry'
import { setObjectToIndexedDB, Stores } from '@/utils/indexedDB/indexed.db'
import { DefaultCanvasBlocks, _migrateDefaultBlockIds, sanitizeBlocks } from '@/utils/CanvasBlocksLayout'
import SlideApi from '@/api/slide'
import Socket from '@/socket.js'

function getImageBlockSrc(block) {
  if (!block || !block.content) return null
  return block.content.src
}

function isLegacyCloudFrontUrl(url) {
  if (!url) return false
  return url.includes('.cloudfront.net')
}

function isExpiredImage(url) {
  try {
    if (!url) return false
    const imgUrl = new URL(url)
    const expires = imgUrl.searchParams.get('Expires')
    if (!expires) return true // nếu ko có param này thì tính là expired image, cần resign lại
    return expires < new Date().getTime() / 1000
  } catch (e) {
    Sentry.captureException(e)
    return false
  }
}

function isImageBlockNeedToResign(block) {
  const imgUrl = getImageBlockSrc(block)
  return isLegacyCloudFrontUrl(imgUrl) || isExpiredImage(imgUrl)
}

export default {
  // NOTES: This file's used for Content slides + import PDF/PPT
  async loadContentSlidesCanvasBlocks(editor, res) {
    const contentSlides = res.body.slides.filter((item) => {
      return item && item.type === 'freestyle'
    })
    const getContentPromises = this._getContentCanvasBlocks(editor,
      contentSlides)

    await Promise.all(getContentPromises).then((data) => {
      this.updateCanvasBlocksArrayToAllSlidesInPresentation(data, editor)
      return true
    })

    await this.updateCanvasBlockForSlideActive(editor, contentSlides)
  },

  _getContentCanvasBlocks(editor, contentSlides) {
    return contentSlides.map(async (s) => {
      if (s.canvasBlocksUrl) {
        try {
          const response = await SlideApi.getCanvasBlocks(s)

          const {
            canvasBlocksWithNewSignedUrls,
            shouldUpdateCanvasBlocks,
          } = await this.resignContentSlideImageUrlsIfExpired(response.body)

          if (shouldUpdateCanvasBlocks) {
            await this._updateCanvasBlocksAfterResignedContentSlideImages(
              canvasBlocksWithNewSignedUrls,
              s.id,
              editor.$store.state.presentation.uniqueAccessCode,
            )
          }

          let canvasBlocks = canvasBlocksWithNewSignedUrls || DefaultCanvasBlocks(s.id)
          canvasBlocks = _migrateDefaultBlockIds(canvasBlocks, s.id)

          setObjectToIndexedDB(s.id, Stores.contentSlideCanvasBlocksStore, {
            canvasBlocks,
          })

          return {
            id: s.id,
            canvasBlocks,
          }
        } catch (error) {
          Sentry.captureException(error)
        }
      }

      let canvasBlocks = DefaultCanvasBlocks(s.id)
      canvasBlocks = _migrateDefaultBlockIds(canvasBlocks, s.id)
      return {
        id: s.id,
        canvasBlocks,
      }
    })
  },

  updateCanvasBlocksArrayToAllSlidesInPresentation(data, editor) {
    // Update canvasBlocksArray to all slides data in the presentation
    editor.$store.state.presentation.slides = editor.$store.state.presentation.slides.map((s) => {
      if (s.type !== 'freestyle') return s
      const slideIndex = data.findIndex((t) => t.id === s.id)
      const dataCanvasBlocks = data[slideIndex] ? data[slideIndex].canvasBlocks : null

      return {
        ...s,
        canvasBlocks: dataCanvasBlocks,
      }
    })
  },

  async updateCanvasBlockForSlideActive(editor, contentSlides) {
    const contentSlideActiveIndex =
      contentSlides.findIndex((item) => item.id === editor.slideActive.id)

    if (contentSlideActiveIndex > -1 && contentSlides[contentSlideActiveIndex]?.canvasBlocksUrl) {
      try {
        const resGetContentSlideActive = await SlideApi.getCanvasBlocks(contentSlides[contentSlideActiveIndex])

        const {
          canvasBlocksWithNewSignedUrls,
        } = await this.resignContentSlideImageUrlsIfExpired(resGetContentSlideActive.body)
        let dataCanvasBlocks = canvasBlocksWithNewSignedUrls || DefaultCanvasBlocks(editor.slideActive.id)

        dataCanvasBlocks = _migrateDefaultBlockIds(dataCanvasBlocks, editor.slideActive.id)

        editor.$store.dispatch('updateSlideActive', {
          canvasBlocks: dataCanvasBlocks,
        })
      } catch (e) {
        Sentry.captureException(e)
      }
    }
  },

  /**
   * @deprecated Please use function from canvasBlocksLoader.utils instead.
   */
  async resignContentSlideImageUrlsIfExpired(blocks) {
    if (!blocks) return null

    const canvasBlocksWithNewSignedUrls = {
      ...blocks,
    }

    const imageBlocks = Object.values(blocks).filter((block) => block.type === 'Image')
    const imageBlocksNeedsToResign = imageBlocks.filter(isImageBlockNeedToResign)
    if (imageBlocksNeedsToResign.length == 0) {
      sanitizeBlocks(canvasBlocksWithNewSignedUrls)
      return {
        canvasBlocksWithNewSignedUrls,
        shouldUpdateCanvasBlocks: false,
      }
    }

    try {
      const arrLinkKey = imageBlocksNeedsToResign.map((block) => block.id)
      const arrayLinkValue = imageBlocksNeedsToResign
        .filter((block) => block.content && block.content.src != null)
        .map((block) => block.content.src)

      const response = await SlideApi.reSignedImages({
        arrLinks: arrayLinkValue,
      })

      if (response && response.length) {
        arrLinkKey.forEach((itemKey, index) => {
          canvasBlocksWithNewSignedUrls[`${itemKey}`].content.src = response[index]
        })
      }
    } catch (error) {
      Sentry.captureException(error)
    }

    sanitizeBlocks(canvasBlocksWithNewSignedUrls)

    return {
      canvasBlocksWithNewSignedUrls,
      shouldUpdateCanvasBlocks: true,
    }
  },

  async _updateCanvasBlocksAfterResignedContentSlideImages(
    blocks,
    slideId,
    uniqueAccessCode,
  ) {
    try {
      const payload = {
        canvasBlocks: blocks,
        id: slideId,
        type: 'freestyle',
        uniqueAccessCode,
        socketId: Socket.getSocketId(),
      }

      await SlideApi.updateSlideById(slideId, payload)
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  isDefaultCanvasBlock(canvasBlockText) {
    try {
      const defaultTitles = ['Click to add question', 'Click to add heading', 'Click to add text', 'Click to add content']
      canvasBlockText = canvasBlockText?.replace(/<[^>]*>?/gm, '')?.trim()
      return defaultTitles.includes(canvasBlockText) || canvasBlockText?.length === 0
    } catch (e) {
      Sentry.captureException(e, { extra: { message: 'Error in isDefaultCanvasBlock' } })
      return false
    }
  },
  isDefaultCanvasBlocks(canvasBlocks) {
    try {
      if (!canvasBlocks) return false
      const expectedHeadingValue = 'Click to add heading'
      const expectedContentValue = 'Click to add content'

      // Check if canvasBlocks is not null and is an object
      if (!canvasBlocks || typeof canvasBlocks !== 'object') {
        return false
      }

      // Extract heading and content block values
      const headingBlock = Object.values(canvasBlocks).find((block) => block.tag === 'heading')
      const contentBlock = Object.values(canvasBlocks).find((block) => block.tag === 'content')

      // Check if heading and content blocks exist and their values match
      return headingBlock && headingBlock.content.value.includes(expectedHeadingValue) &&
           contentBlock && contentBlock.content.value.includes(expectedContentValue)
    } catch (error) {
      Sentry.captureException(error)
      return false
    }
  },
}
